import { useState } from "react";
import axios from "axios";
import { CheckCircle2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import FadeUp from "../components/site/FadeUp";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { TOWNS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  email: "",
  phone: "",
  town: "",
  experience: "",
  message: "",
};

export default function Careers() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, { ...form, type: "application" });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <section className="section-pad pb-8">
        <div className="container-hh max-w-3xl">
          <FadeUp>
            <p className="eyebrow">Join our team</p>
            <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl">
              Work with Harbor Home.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-bodytext">
              We're always looking for caring, reliable people who share our
              passion for helping busy families. If you take pride in a job well
              done and love bringing calm to a home, we'd love to hear from you.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-navy">
              <HeartHandshake className="h-5 w-5 text-teal" /> We respond to every
              application personally.
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="section-pad pt-4">
        <div className="container-hh max-w-3xl">
          {done ? (
            <FadeUp
              data-testid="application-confirmation"
              className="cozy-card flex flex-col items-center gap-4 p-10 text-center"
            >
              <CheckCircle2 className="h-14 w-14 text-teal" />
              <h2 className="text-3xl">Thank you for applying!</h2>
              <p className="max-w-md text-bodytext">
                We've received your application and{" "}
                <strong className="text-navy">will be in touch soon</strong>. Thank
                you for your interest in joining the Harbor Home team.
              </p>
            </FadeUp>
          ) : (
            <FadeUp>
              <form
                data-testid="application-form"
                onSubmit={handleSubmit}
                className="cozy-card p-6 md:p-8"
              >
                <h2 className="text-2xl md:text-3xl">Apply to join our team</h2>
                <p className="mt-1 text-sm text-bodytext/70">
                  Tell us a little about yourself — we read every application.
                </p>

                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field label="Name" required>
                    <Input
                      data-testid="app-name"
                      value={form.name}
                      onChange={onInput("name")}
                      placeholder="Your name"
                      required
                    />
                  </Field>
                  <Field label="Email" required>
                    <Input
                      data-testid="app-email"
                      type="email"
                      value={form.email}
                      onChange={onInput("email")}
                      placeholder="you@email.com"
                      required
                    />
                  </Field>
                  <Field label="Phone" required>
                    <Input
                      data-testid="app-phone"
                      value={form.phone}
                      onChange={onInput("phone")}
                      placeholder="(781) 555-0123"
                      required
                    />
                  </Field>
                  <Field label="Town you're near">
                    <Select value={form.town} onValueChange={set("town")}>
                      <SelectTrigger data-testid="app-town" className="rounded-[8px]">
                        <SelectValue placeholder="Select your town" />
                      </SelectTrigger>
                      <SelectContent>
                        {TOWNS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}, MA
                          </SelectItem>
                        ))}
                        <SelectItem value="Other">Other / nearby</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="Relevant experience">
                    <Textarea
                      data-testid="app-experience"
                      value={form.experience}
                      onChange={onInput("experience")}
                      placeholder="Home care, cleaning, organizing, childcare, hospitality — anything relevant."
                      rows={3}
                    />
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="Why would you be a great fit?">
                    <Textarea
                      data-testid="app-message"
                      value={form.message}
                      onChange={onInput("message")}
                      placeholder="Tell us what draws you to this work and what makes you great at it."
                      rows={4}
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  data-testid="app-submit"
                  disabled={submitting}
                  className="btn-teal mt-6 w-full disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit My Application"}
                </button>
              </form>
            </FadeUp>
          )}
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <Label className="mb-1.5 block text-sm font-medium text-navy">
      {label} {required && <span className="text-teal">*</span>}
    </Label>
    {children}
  </div>
);
